// =============================================================================
// Origin UI — src/pages/creditcards/CreditCardReportsPage.tsx
// CC Dashboard: filters at top, charts (USD reporting), filterable transaction table.
// All aggregates (stat cards, charts) use USD as reporting currency.
// Transaction table shows original currency + USD equivalent.
// =============================================================================

import { useState, useEffect, useMemo } from 'react';
import { PageHeader, Card, ErrorBanner, LoadingSpinner } from '@/components/common';
import { getPeriods, type StatementPeriodListItem } from '@/services/creditCardApi';
import { api } from '@/services/apiService';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import styles from './creditcards.module.css';

interface ReportTransaction {
  cardTransactionId: string;
  corporateCardId: string | null;
  transactionDate: string;
  merchantName: string;
  merchantCategory: string;
  amount: number;
  currency: string;
  amountUsd: number;
  cardLastFourDigits: string;
  cardIssuer: string;
  cardholderName: string;
  employeeNumber: string;
  commentary: string | null;
  isFlaggedPersonal: boolean;
  isFlaggedDisputed: boolean;
  costCentre: string;
}

interface ReportReview {
  corporateCardId: string;
  cardholderName: string;
  approverName: string;
  status: string;
  transactionCount: number;
  totalAmount: number;
  totalAmountUsd: number;
  cardCurrency: string;
  cardIssuer: string;
  cardLastFour: string;
}

interface ReportData {
  periodName: string;
  periodStatus: string;
  transactions: ReportTransaction[];
  reviews: ReportReview[];
}

type SortKey = 'transactionDate' | 'cardholderName' | 'merchantName' | 'amount' | 'merchantCategory' | 'cardIssuer';
type SortDir = 'asc' | 'desc';

function fmtUsd(value: number) {
  return 'USD ' + value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function fmtOriginal(amount: number, currency: string) {
  return currency + ' ' + amount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

export function CreditCardReportsPage() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [periods, setPeriods] = useState<StatementPeriodListItem[]>([]);
  const [selectedPeriodId, setSelectedPeriodId] = useState('');
  const [reportData, setReportData] = useState<ReportData | null>(null);
  const [loadingReport, setLoadingReport] = useState(false);

  // Filters
  const [filterCardholder, setFilterCardholder] = useState('');
  const [filterCategory, setFilterCategory] = useState('');
  const [filterApprover, setFilterApprover] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterBank, setFilterBank] = useState('');
  const [filterMinAmount, setFilterMinAmount] = useState('');
  const [filterMaxAmount, setFilterMaxAmount] = useState('');
  const [filterPersonal, setFilterPersonal] = useState<'' | 'yes' | 'no'>('');
  const [filterDisputed, setFilterDisputed] = useState<'' | 'yes' | 'no'>('');
  const [filterMatched, setFilterMatched] = useState<'' | 'matched' | 'unmatched'>('');
  const [showFilters, setShowFilters] = useState(true);

  // Sort
  const [sortKey, setSortKey] = useState<SortKey>('transactionDate');
  const [sortDir, setSortDir] = useState<SortDir>('asc');

  useEffect(() => { loadPeriods(); }, []);

  async function loadPeriods() {
    try {
      setLoading(true);
      const p = await getPeriods();
      setPeriods(p);
      if (p.length > 0) setSelectedPeriodId(p[0].statementPeriodId);
    } catch (e: any) { setError(e.message); }
    finally { setLoading(false); }
  }

  useEffect(() => { if (selectedPeriodId) loadReport(); }, [selectedPeriodId]);

  async function loadReport() {
    try {
      setLoadingReport(true);
      setError('');
      const data = await api.get<ReportData>(`/api/credit-card-reports/${selectedPeriodId}/transactions`);
      setReportData(data);
    } catch (e: any) { setError(e.message); }
    finally { setLoadingReport(false); }
  }

  // Dropdown options
  const cardholderOptions = useMemo(() => {
    if (!reportData) return [];
    return [...new Set(reportData.transactions.map(t => t.cardholderName))].sort();
  }, [reportData]);

  const categoryOptions = useMemo(() => {
    if (!reportData) return [];
    return [...new Set(reportData.transactions.map(t => t.merchantCategory))].sort();
  }, [reportData]);

  const approverOptions = useMemo(() => {
    if (!reportData) return [];
    return [...new Set(reportData.reviews.map(r => r.approverName))].sort();
  }, [reportData]);

  const bankOptions = useMemo(() => {
    if (!reportData) return [];
    return [...new Set(reportData.transactions.map(t => t.cardIssuer))].sort();
  }, [reportData]);

  const approverMap = useMemo(() => {
    if (!reportData) return {};
    const map: Record<string, string> = {};
    reportData.reviews.forEach(r => { map[r.cardholderName] = r.approverName; });
    return map;
  }, [reportData]);

  const statusMap = useMemo(() => {
    if (!reportData) return {};
    const map: Record<string, string> = {};
    reportData.reviews.forEach(r => { map[r.cardholderName] = r.status; });
    return map;
  }, [reportData]);


  const filtered = useMemo(() => {
    if (!reportData) return [];
    let txns = reportData.transactions;

    if (filterCardholder) txns = txns.filter(t => t.cardholderName === filterCardholder);
    if (filterCategory) txns = txns.filter(t => t.merchantCategory === filterCategory);
    if (filterBank) txns = txns.filter(t => t.cardIssuer === filterBank);
    if (filterApprover) {
      const names = Object.entries(approverMap).filter(([, v]) => v === filterApprover).map(([k]) => k);
      txns = txns.filter(t => names.includes(t.cardholderName));
    }
    if (filterStatus) {
      const names = Object.entries(statusMap).filter(([, v]) => v === filterStatus).map(([k]) => k);
      txns = txns.filter(t => names.includes(t.cardholderName));
    }
    if (filterMinAmount) txns = txns.filter(t => t.amountUsd >= Number(filterMinAmount));
    if (filterMaxAmount) txns = txns.filter(t => t.amountUsd <= Number(filterMaxAmount));
    if (filterPersonal === 'yes') txns = txns.filter(t => t.isFlaggedPersonal);
    if (filterPersonal === 'no') txns = txns.filter(t => !t.isFlaggedPersonal);
    if (filterDisputed === 'yes') txns = txns.filter(t => t.isFlaggedDisputed);
    if (filterMatched === 'matched') txns = txns.filter(t => t.corporateCardId != null);
    if (filterMatched === 'unmatched') txns = txns.filter(t => t.corporateCardId == null);
    if (filterDisputed === 'no') txns = txns.filter(t => !t.isFlaggedDisputed);

    txns = [...txns].sort((a, b) => {
      let cmp = 0;
      if (sortKey === 'amount') cmp = a.amountUsd - b.amountUsd;
      else cmp = (a[sortKey] ?? '').localeCompare(b[sortKey] ?? '');
      return sortDir === 'asc' ? cmp : -cmp;
    });

    return txns;
  }, [reportData, filterCardholder, filterCategory, filterApprover, filterStatus, filterBank, filterMinAmount, filterMaxAmount, filterPersonal, filterDisputed, filterMatched, sortKey, sortDir, approverMap, statusMap]);

  // Charts use USD amounts
  const spendByCardholder = useMemo(() => {
    if (!reportData) return [];
    const map: Record<string, number> = {};
    filtered.forEach(t => { map[t.cardholderName] = (map[t.cardholderName] || 0) + t.amountUsd; });
    return Object.entries(map).map(([name, total]) => ({ name: name, fullName: name, total: Math.round(total * 100) / 100 }))
      .sort((a, b) => b.total - a.total);
  }, [filtered, reportData]);

  const spendByCategory = useMemo(() => {
    if (!reportData) return [];
    const map: Record<string, number> = {};
    filtered.forEach(t => { map[t.merchantCategory] = (map[t.merchantCategory] || 0) + t.amountUsd; });
    return Object.entries(map).map(([name, value]) => ({ name: name.length > 25 ? name.slice(0, 25) + '...' : name, fullName: name, value: Math.round(value * 100) / 100 }))
      .sort((a, b) => b.value - a.value).slice(0, 6);
  }, [filtered, reportData]);

  function toggleSort(key: SortKey) {
    if (sortKey === key) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortKey(key); setSortDir('asc'); }
  }

  function sortIcon(key: SortKey) {
    if (sortKey !== key) return ' \u21D5';
    return sortDir === 'asc' ? ' \u2191' : ' \u2193';
  }

  // All aggregates in USD
  const totalSpendUsd = filtered.reduce((s, t) => s + t.amountUsd, 0);
  const avgTransactionUsd = filtered.length > 0 ? totalSpendUsd / filtered.length : 0;
  const personalTotalUsd = filtered.filter(t => t.isFlaggedPersonal).reduce((s, t) => s + t.amountUsd, 0);
  const disputedTotalUsd = filtered.filter(t => t.isFlaggedDisputed).reduce((s, t) => s + t.amountUsd, 0);

  // Submission completion
  const submittedCardIds = new Set(reportData?.reviews.filter(r => r.status === 'Submitted' || r.status === 'Approved').map(r => r.corporateCardId) || []);
  const submittedTxns = filtered.filter(t => t.corporateCardId != null && submittedCardIds.has(t.corporateCardId)).length;
  const completionPct = filtered.length > 0 ? Math.round((submittedTxns / filtered.length) * 100) : 0;

  function clearFilters() {
    setFilterCardholder(''); setFilterCategory(''); setFilterApprover('');
    setFilterStatus(''); setFilterBank(''); setFilterMinAmount(''); setFilterMaxAmount('');
    setFilterPersonal(''); setFilterDisputed(''); setFilterMatched('');
  }

  const hasActiveFilters = filterCardholder || filterCategory || filterApprover || filterStatus || filterBank || filterMinAmount || filterMaxAmount || filterPersonal || filterDisputed || filterMatched;

  if (loading) return <LoadingSpinner />;

  return (
    <div>
      <PageHeader title="Credit Card Reports" subtitle="Spend analysis, charts, and transaction drill-down" />
      {error && <ErrorBanner message={error} onDismiss={() => setError('')} />}

      {/* Period selector + filters toggle */}
      <div style={{ marginBottom: 16, display: 'flex', gap: 12, alignItems: 'center', justifyContent: 'space-between' }}>
        <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
          <label style={{ fontSize: 13, fontWeight: 600, color: 'var(--navy)' }}>Period:</label>
          <select className={styles.fieldInput} style={{ width: 220 }} value={selectedPeriodId} onChange={e => setSelectedPeriodId(e.target.value)}>
            {periods.map(p => <option key={p.statementPeriodId} value={p.statementPeriodId}>{p.periodName} ({p.status})</option>)}
          </select>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          {hasActiveFilters && <button onClick={clearFilters} style={{ fontSize: 11, color: 'var(--red)', background: 'none', border: 'none', cursor: 'pointer', fontWeight: 600 }}>Clear filters</button>}
          <button onClick={() => setShowFilters(!showFilters)} style={{ fontSize: 12, fontWeight: 600, padding: '6px 14px', borderRadius: 6, cursor: 'pointer', background: showFilters ? 'var(--turquoise-light)' : 'var(--gray-100)', color: showFilters ? 'var(--turquoise-dark)' : 'var(--gray-600)', border: '1px solid ' + (showFilters ? 'var(--turquoise)' : 'var(--gray-200)') }}>
            {showFilters ? 'Hide Filters' : 'Filters'}
          </button>
        </div>
      </div>

      {/* Filters panel at top */}
      {showFilters && reportData && (
        <div style={{ marginBottom: 16 }}>
        <Card>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 10 }}>
            <div><label style={{ fontSize: 11, fontWeight: 600, color: 'var(--gray-500)', display: 'block', marginBottom: 4 }}>Cardholder</label>
              <select className={styles.fieldInput} style={{ fontSize: 12 }} value={filterCardholder} onChange={e => setFilterCardholder(e.target.value)}>
                <option value="">All</option>
                {cardholderOptions.map(n => <option key={n} value={n}>{n}</option>)}
              </select>
            </div>
            <div><label style={{ fontSize: 11, fontWeight: 600, color: 'var(--gray-500)', display: 'block', marginBottom: 4 }}>Category</label>
              <select className={styles.fieldInput} style={{ fontSize: 12 }} value={filterCategory} onChange={e => setFilterCategory(e.target.value)}>
                <option value="">All</option>
                {categoryOptions.map(n => <option key={n} value={n}>{n}</option>)}
              </select>
            </div>
            <div><label style={{ fontSize: 11, fontWeight: 600, color: 'var(--gray-500)', display: 'block', marginBottom: 4 }}>Approver</label>
              <select className={styles.fieldInput} style={{ fontSize: 12 }} value={filterApprover} onChange={e => setFilterApprover(e.target.value)}>
                <option value="">All</option>
                {approverOptions.map(n => <option key={n} value={n}>{n}</option>)}
              </select>
            </div>
            <div><label style={{ fontSize: 11, fontWeight: 600, color: 'var(--gray-500)', display: 'block', marginBottom: 4 }}>Bank</label>
              <select className={styles.fieldInput} style={{ fontSize: 12 }} value={filterBank} onChange={e => setFilterBank(e.target.value)}>
                <option value="">All</option>
                {bankOptions.map(n => <option key={n} value={n}>{n}</option>)}
              </select>
            </div>
            <div><label style={{ fontSize: 11, fontWeight: 600, color: 'var(--gray-500)', display: 'block', marginBottom: 4 }}>Status</label>
              <select className={styles.fieldInput} style={{ fontSize: 12 }} value={filterStatus} onChange={e => setFilterStatus(e.target.value)}>
                <option value="">All</option><option value="PendingReview">Pending Review</option><option value="Submitted">Submitted</option><option value="Approved">Approved</option><option value="Returned">Returned</option>
              </select>
            </div>
            <div><label style={{ fontSize: 11, fontWeight: 600, color: 'var(--gray-500)', display: 'block', marginBottom: 4 }}>Min Amount (USD)</label><input className={styles.fieldInput} style={{ fontSize: 12 }} type="number" placeholder="0" value={filterMinAmount} onChange={e => setFilterMinAmount(e.target.value)} /></div>
            <div><label style={{ fontSize: 11, fontWeight: 600, color: 'var(--gray-500)', display: 'block', marginBottom: 4 }}>Max Amount (USD)</label><input className={styles.fieldInput} style={{ fontSize: 12 }} type="number" placeholder="999999" value={filterMaxAmount} onChange={e => setFilterMaxAmount(e.target.value)} /></div>
            <div><label style={{ fontSize: 11, fontWeight: 600, color: 'var(--gray-500)', display: 'block', marginBottom: 4 }}>Personal</label>
              <select className={styles.fieldInput} style={{ fontSize: 12 }} value={filterPersonal} onChange={e => setFilterPersonal(e.target.value as '' | 'yes' | 'no')}>
                <option value="">All</option><option value="yes">Flagged</option><option value="no">Not flagged</option>
              </select>
            </div>
            <div><label style={{ fontSize: 11, fontWeight: 600, color: 'var(--gray-500)', display: 'block', marginBottom: 4 }}>Disputed</label>
              <select className={styles.fieldInput} style={{ fontSize: 12 }} value={filterDisputed} onChange={e => setFilterDisputed(e.target.value as '' | 'yes' | 'no')}>
                <option value="">All</option><option value="yes">Flagged</option><option value="no">Not flagged</option>
              </select>
            </div>
            <div><label style={{ fontSize: 11, fontWeight: 600, color: 'var(--gray-500)', display: 'block', marginBottom: 4 }}>Matched</label>
              <select className={styles.fieldInput} style={{ fontSize: 12 }} value={filterMatched} onChange={e => setFilterMatched(e.target.value as '' | 'matched' | 'unmatched')}>
                <option value="">All</option><option value="matched">Matched</option><option value="unmatched">Unmatched</option>
              </select>
            </div>
          </div>
        </Card>
        </div>
      )}

      {loadingReport ? <LoadingSpinner /> : reportData && (
        <>
          {/* Summary stats — all in USD */}
          <div className={styles.statCards} style={{ marginBottom: 20 }}>
            <div className={styles.statCard}><div className={styles.statCardValue}>{filtered.length}</div><div className={styles.statCardLabel}>Transactions</div></div>
            <div className={styles.statCard}><div className={styles.statCardValue} style={{ color: completionPct === 100 ? 'var(--green)' : 'var(--orange)' }}>{submittedTxns} <span style={{ fontSize: 14 }}>({completionPct}%)</span></div><div className={styles.statCardLabel}>Submitted</div></div>
            <div className={styles.statCard}><div className={styles.statCardValue}>{fmtUsd(totalSpendUsd)}</div><div className={styles.statCardLabel}>Total Spend</div></div>
            <div className={styles.statCard}><div className={styles.statCardValue}>{fmtUsd(avgTransactionUsd)}</div><div className={styles.statCardLabel}>Avg Transaction</div></div>
            <div className={styles.statCard}><div className={styles.statCardValue} style={{ color: 'var(--purple)' }}>{fmtUsd(personalTotalUsd)}</div><div className={styles.statCardLabel}>Personal</div></div>
            <div className={styles.statCard}><div className={styles.statCardValue} style={{ color: 'var(--red)' }}>{fmtUsd(disputedTotalUsd)}</div><div className={styles.statCardLabel}>Disputed</div></div>
          </div>

          {/* Charts — USD reporting currency */}
          <div style={{ display: 'grid', gridTemplateColumns: '3fr 2fr', gap: 16, marginBottom: 20 }}>
            <Card>
              <h4 style={{ margin: '0 0 12px', fontSize: 14, color: 'var(--navy)' }}>Spend by Cardholder <span style={{ fontSize: 11, fontWeight: 400, color: 'var(--gray-400)' }}>(USD)</span></h4>
              {(() => {
                const dataMax = Math.max(...spendByCardholder.map(s => s.total), 1);
                const axisMax = Math.ceil(dataMax / 10000) * 10000;
                const ticks = Array.from({length: Math.floor(axisMax / 10000) + 1}, (_, i) => i * 10000);
                const needsScroll = dataMax > 50000;
                const chartH = Math.max(260, spendByCardholder.length * 28);
                const fixedW = needsScroll ? Math.round(axisMax / 50000 * 700) + 90 : undefined;
                const chart = (
                  <BarChart data={spendByCardholder} layout="vertical" width={fixedW || 600} height={chartH} margin={{ left: 10, right: 20, top: 5, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E2E7EC" horizontal={false} />
                    <XAxis type="number" hide domain={[0, axisMax]} ticks={ticks} />
                    <YAxis type="category" dataKey="name" width={140} tick={{ fontSize: 11, fill: '#0A2240' }} />
                    <Tooltip formatter={(v) => [fmtUsd(Number(v)), 'Spend']} labelFormatter={(_l, payload) => payload?.[0]?.payload?.fullName || _l} />
                    <Bar dataKey="total" fill="#FF8300" radius={[0, 4, 4, 0]} />
                  </BarChart>
                );
                return (
                  <>
                    <div style={{ maxHeight: 380, overflowY: 'auto', overflowX: needsScroll ? 'scroll' : 'hidden' }}>
                      {needsScroll ? chart : <ResponsiveContainer width="100%" height={chartH}>{chart}</ResponsiveContainer>}
                    </div>
                    <div style={{ overflowX: needsScroll ? 'scroll' : 'hidden', marginLeft: 80, marginRight: 20, borderTop: '1px solid var(--gray-200)' }}>
                      <div style={{ width: needsScroll ? fixedW! - 90 : undefined, display: 'flex', justifyContent: 'space-between', padding: '4px 0', fontSize: 11, color: '#6B7885' }}>
                        {ticks.map((v, idx) => <span key={idx}>{v.toLocaleString()}</span>)}
                      </div>
                    </div>
                  </>
                );
              })()}
            </Card>
            <Card>
              <h4 style={{ margin: '0 0 12px', fontSize: 14, color: 'var(--navy)' }}>Top Categories <span style={{ fontSize: 11, fontWeight: 400, color: 'var(--gray-400)' }}>(USD)</span></h4>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {spendByCategory.map((c, i) => {
                  const maxVal = spendByCategory[0]?.value || 1;
                  const pct = (c.value / maxVal) * 100;
                  return (
                    <div key={i}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, marginBottom: 2 }}>
                        <span style={{ color: 'var(--gray-600)' }}>{c.fullName}</span>
                        <span style={{ fontWeight: 600, color: 'var(--navy)' }}>{fmtUsd(c.value)}</span>
                      </div>
                      <div style={{ height: 6, background: 'var(--gray-100)', borderRadius: 3 }}>
                        <div style={{ height: 6, background: 'var(--turquoise)', borderRadius: 3, width: `${pct}%`, transition: 'width 0.3s ease' }} />
                      </div>
                    </div>
                  );
                })}
                {spendByCategory.length === 0 && <p style={{ fontSize: 12, color: 'var(--gray-400)', textAlign: 'center', padding: 20 }}>No data</p>}
              </div>
            </Card>
          </div>

          {/* Transaction table — original currency + USD equivalent */}
          <Card padding={false}>
            <div style={{ padding: '12px 16px', borderBottom: '1px solid var(--gray-200)' }}>
              <h4 style={{ margin: 0, fontSize: 14, color: 'var(--navy)' }}>
                Transactions {hasActiveFilters && <span style={{ fontSize: 12, color: 'var(--turquoise)', marginLeft: 8 }}>({filtered.length} of {reportData.transactions.length})</span>}
              </h4>
            </div>
            <div className={styles.txTableWrap}>
              <table className={styles.txTable}>
                <thead>
                  <tr>
                    <th style={{ cursor: 'pointer' }} onClick={() => toggleSort('transactionDate')}>Date{sortIcon('transactionDate')}</th>
                    <th style={{ cursor: 'pointer' }} onClick={() => toggleSort('cardholderName')}>Cardholder{sortIcon('cardholderName')}</th>
                    <th style={{ cursor: 'pointer' }} onClick={() => toggleSort('cardIssuer')}>Bank{sortIcon('cardIssuer')}</th>
                    <th style={{ cursor: 'pointer' }} onClick={() => toggleSort('merchantName')}>Merchant{sortIcon('merchantName')}</th>
                    <th style={{ cursor: 'pointer' }} onClick={() => toggleSort('merchantCategory')}>Category{sortIcon('merchantCategory')}</th>
                    <th style={{ textAlign: 'right' }}>Original</th>
                    <th style={{ textAlign: 'right', cursor: 'pointer' }} onClick={() => toggleSort('amount')}>USD{sortIcon('amount')}</th>
                    <th>Commentary</th>
                    <th style={{ textAlign: 'center' }}>Flags</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.slice(0, 200).map(t => (
                    <tr key={t.cardTransactionId}>
                      <td style={{ fontSize: 12, whiteSpace: 'nowrap' }}>{new Date(t.transactionDate).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' })}</td>
                      <td style={{ fontWeight: 600, fontSize: 13 }}>{t.cardholderName}</td>
                      <td style={{ fontSize: 12 }}>{t.cardIssuer}</td>
                      <td style={{ fontSize: 12 }}>{t.merchantName}</td>
                      <td style={{ fontSize: 12, color: 'var(--gray-500)' }}>{t.merchantCategory}</td>
                      <td style={{ textAlign: 'right', fontSize: 12, whiteSpace: 'nowrap', color: 'var(--gray-500)' }}>{fmtOriginal(t.amount, t.currency)}</td>
                      <td style={{ textAlign: 'right', fontWeight: 600, fontSize: 13, whiteSpace: 'nowrap' }}>{t.amountUsd.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                      <td style={{ fontSize: 12, color: 'var(--gray-600)', maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{t.commentary || '\u2014'}</td>
                      <td style={{ textAlign: 'center', fontSize: 10 }}>
                        {t.isFlaggedPersonal && <span style={{ display: 'inline-block', padding: '1px 5px', borderRadius: 3, background: 'var(--purple-light)', color: 'var(--purple)', fontWeight: 600, marginRight: 2 }}>P</span>}
                        {t.isFlaggedDisputed && <span style={{ display: 'inline-block', padding: '1px 5px', borderRadius: 3, background: 'var(--red-light)', color: '#C33A30', fontWeight: 600 }}>D</span>}
                      </td>
                    </tr>
                  ))}
                  {filtered.length === 0 && <tr><td colSpan={9} style={{ textAlign: 'center', padding: 32, color: 'var(--gray-400)' }}>No transactions match filters.</td></tr>}
                  {filtered.length > 200 && <tr><td colSpan={9} style={{ textAlign: 'center', padding: 12, color: 'var(--gray-400)', fontSize: 12 }}>Showing first 200 of {filtered.length} transactions.</td></tr>}
                </tbody>
              </table>
            </div>
          </Card>
        </>
      )}
    </div>
  );
}
